/**
 * Sezione "Reseller" del pannello admin.
 *
 * Mostra una tabella reseller con CRUD + drill-down per visualizzare
 * la lista utenti di uno specifico reseller (admin override su isolamento).
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Plus, Edit, Trash2, Search, Store, Users as UsersIcon,
  X, Check, Lock, Unlock, ArrowLeft, AlertTriangle
} from "lucide-react";

const BACKEND_URL = (typeof window !== 'undefined' && process.env.NODE_ENV === 'production')
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${BACKEND_URL}/api`;

export default function ResellersPage() {
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingReseller, setEditingReseller] = useState(null);
  const [drillReseller, setDrillReseller] = useState(null); // se !== null mostra utenti
  const [toast, setToast] = useState(null);
  const [allDns, setAllDns] = useState([]);

  const [createForm, setCreateForm] = useState({
    username: "", password: "", full_name: "", email: "",
    expiry_threshold_days: 7, max_users: "", allowed_dns_ids: []
  });
  const [editForm, setEditForm] = useState({
    password: "", full_name: "", email: "", is_active: true,
    expiry_threshold_days: 7, max_users: "", allowed_dns_ids: []
  });

  const fetchResellers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/resellers`, {
        params: search ? { search } : {}
      });
      setResellers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setToast({ type: "error", msg: "Errore nel caricamento reseller" });
    }
    setLoading(false);
  }, [search]);

  const fetchDns = async () => {
    try {
      const res = await axios.get(`${API}/dns`);
      setAllDns(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setAllDns([]); }
  };

  useEffect(() => { fetchResellers(); }, [fetchResellers]);
  useEffect(() => { fetchDns(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...createForm };
      if (!payload.max_users) delete payload.max_users;
      else payload.max_users = parseInt(payload.max_users, 10);
      payload.expiry_threshold_days = parseInt(payload.expiry_threshold_days, 10) || 7;
      payload.allowed_dns_ids = createForm.allowed_dns_ids || [];
      await axios.post(`${API}/admin/resellers`, payload);
      setToast({ type: "success", msg: "Reseller creato!" });
      setShowCreateModal(false);
      setCreateForm({ username: "", password: "", full_name: "", email: "",
        expiry_threshold_days: 7, max_users: "", allowed_dns_ids: [] });
      fetchResellers();
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    }
  };

  const openEdit = (r) => {
    setEditingReseller(r);
    setEditForm({
      password: "",
      full_name: r.full_name || "",
      email: r.email || "",
      is_active: r.is_active,
      expiry_threshold_days: r.expiry_threshold_days,
      max_users: r.max_users || "",
      allowed_dns_ids: r.allowed_dns_ids || [],
      vpn_popup_enabled: r.vpn_popup_enabled !== false
    });
    setShowEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      if (editForm.password) payload.password = editForm.password;
      if (editForm.full_name !== editingReseller.full_name) payload.full_name = editForm.full_name;
      if (editForm.email !== editingReseller.email) payload.email = editForm.email;
      if (editForm.is_active !== editingReseller.is_active) payload.is_active = editForm.is_active;
      if (editForm.expiry_threshold_days !== editingReseller.expiry_threshold_days) {
        payload.expiry_threshold_days = parseInt(editForm.expiry_threshold_days, 10);
      }
      if (String(editForm.max_users || "") !== String(editingReseller.max_users || "")) {
        payload.max_users = editForm.max_users ? parseInt(editForm.max_users, 10) : null;
      }
      // allowed_dns_ids: invia sempre (anche array vuoto) se differisce
      const currentDns = editingReseller.allowed_dns_ids || [];
      const newDns = editForm.allowed_dns_ids || [];
      const dnsChanged = currentDns.length !== newDns.length ||
        currentDns.some((id) => !newDns.includes(id));
      if (dnsChanged) payload.allowed_dns_ids = newDns;
      if (editForm.vpn_popup_enabled !== (editingReseller.vpn_popup_enabled !== false)) {
        payload.vpn_popup_enabled = editForm.vpn_popup_enabled;
      }
      await axios.patch(`${API}/admin/resellers/${editingReseller.id}`, payload);
      setToast({ type: "success", msg: "Reseller aggiornato!" });
      setShowEditModal(false);
      fetchResellers();
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    }
  };

  const toggleSuspend = async (r) => {
    try {
      await axios.patch(`${API}/admin/resellers/${r.id}`, { is_active: !r.is_active });
      setToast({
        type: "success",
        msg: r.is_active ? "Reseller sospeso" : "Reseller riattivato"
      });
      fetchResellers();
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(
      `Eliminare il reseller "${r.username}"?\n` +
      `Verranno eliminati anche TUTTI i suoi ${r.users_count} utenti (soft-delete).`
    )) return;
    try {
      await axios.delete(`${API}/admin/resellers/${r.id}`);
      setToast({ type: "success", msg: "Reseller eliminato" });
      fetchResellers();
    } catch (err) {
      setToast({ type: "error", msg: err.response?.data?.detail || "Errore" });
    }
  };

  if (drillReseller) {
    return <ResellerUsersView reseller={drillReseller} onBack={() => setDrillReseller(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Store className="text-netflix-red" />
            Reseller
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">
            Gestisci i reseller. Ognuno ha un proprio pannello e crea i suoi utenti.
          </p>
        </div>
        <button
          data-testid="add-reseller-btn"
          onClick={() => setShowCreateModal(true)}
          className="bg-netflix-red hover:bg-red-700 px-5 py-2 rounded-sm font-bold flex items-center gap-2 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nuovo Reseller
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          data-testid="search-resellers"
          type="text"
          placeholder="Cerca per username, nome, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-sm focus:border-netflix-red outline-none text-sm"
        />
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Username</th>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Utenti</th>
              <th className="text-left px-4 py-3">Soglia Scad.</th>
              <th className="text-left px-4 py-3">Stato</th>
              <th className="text-right px-4 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr><td colSpan="7" className="text-center py-8 text-zinc-500">Caricamento…</td></tr>
            ) : resellers.length === 0 ? (
              <tr><td colSpan="7" className="text-center py-8 text-zinc-500">
                Nessun reseller. Premi "Nuovo Reseller" per iniziare.
              </td></tr>
            ) : resellers.map((r) => (
              <tr key={r.id} className="hover:bg-zinc-900/50 transition-colors">
                <td className="px-4 py-3 font-mono text-sm font-semibold">{r.username}</td>
                <td className="px-4 py-3 text-sm text-zinc-300">{r.full_name || "—"}</td>
                <td className="px-4 py-3 text-sm text-zinc-400">{r.email || "—"}</td>
                <td className="px-4 py-3 text-sm">
                  <button
                    data-testid={`view-users-${r.username}`}
                    onClick={() => setDrillReseller(r)}
                    className="text-netflix-red hover:underline flex items-center gap-1"
                  >
                    <UsersIcon className="w-3 h-3" />
                    {r.active_users_count}/{r.users_count}
                    {r.max_users ? ` (max ${r.max_users})` : ""}
                  </button>
                </td>
                <td className="px-4 py-3 text-sm">{r.expiry_threshold_days}gg</td>
                <td className="px-4 py-3">
                  {r.is_active ? (
                    <span className="text-green-500 text-xs font-bold">● ATTIVO</span>
                  ) : (
                    <span className="text-zinc-500 text-xs font-bold">● SOSPESO</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button
                      data-testid={`toggle-suspend-${r.username}`}
                      onClick={() => toggleSuspend(r)}
                      className="p-2 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white"
                      title={r.is_active ? "Sospendi" : "Riattiva"}
                    >
                      {r.is_active ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                    </button>
                    <button
                      data-testid={`edit-${r.username}`}
                      onClick={() => openEdit(r)}
                      className="p-2 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white"
                      title="Modifica"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-${r.username}`}
                      onClick={() => handleDelete(r)}
                      className="p-2 hover:bg-red-900/40 rounded text-zinc-400 hover:text-netflix-red"
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CREATE MODAL */}
      {showCreateModal && (
        <Modal title="Nuovo Reseller" onClose={() => setShowCreateModal(false)}>
          <form onSubmit={handleCreate} className="space-y-4">
            <Input label="Username" required value={createForm.username}
                   onChange={(v) => setCreateForm({...createForm, username: v})}
                   testId="reseller-username" />
            <Input label="Password (min 8)" type="password" required value={createForm.password}
                   onChange={(v) => setCreateForm({...createForm, password: v})}
                   testId="reseller-password" />
            <Input label="Nome completo" value={createForm.full_name}
                   onChange={(v) => setCreateForm({...createForm, full_name: v})}
                   testId="reseller-fullname" />
            <Input label="Email" type="email" value={createForm.email}
                   onChange={(v) => setCreateForm({...createForm, email: v})}
                   testId="reseller-email" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Soglia scadenza (gg)" type="number" required
                     value={createForm.expiry_threshold_days}
                     onChange={(v) => setCreateForm({...createForm, expiry_threshold_days: v})}
                     testId="reseller-threshold" />
              <Input label="Max utenti (vuoto = illimitato)" type="number"
                     value={createForm.max_users}
                     onChange={(v) => setCreateForm({...createForm, max_users: v})}
                     testId="reseller-maxusers" />
            </div>
            <DnsMultiSelect
              label="DNS assegnati a questo reseller"
              allDns={allDns}
              selected={createForm.allowed_dns_ids}
              onChange={(ids) => setCreateForm({...createForm, allowed_dns_ids: ids})}
              testId="reseller-create-dns" />
            <ModalButtons onCancel={() => setShowCreateModal(false)} submitText="Crea Reseller"
                          testId="reseller-create-submit" />
          </form>
        </Modal>
      )}

      {/* EDIT MODAL */}
      {showEditModal && editingReseller && (
        <Modal title={`Modifica ${editingReseller.username}`} onClose={() => setShowEditModal(false)}>
          <form onSubmit={handleEdit} className="space-y-4">
            <Input label="Nuova password (lascia vuoto per non cambiare)" type="password"
                   value={editForm.password}
                   onChange={(v) => setEditForm({...editForm, password: v})}
                   testId="edit-reseller-password" />
            <Input label="Nome completo" value={editForm.full_name}
                   onChange={(v) => setEditForm({...editForm, full_name: v})}
                   testId="edit-reseller-fullname" />
            <Input label="Email" type="email" value={editForm.email}
                   onChange={(v) => setEditForm({...editForm, email: v})}
                   testId="edit-reseller-email" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Soglia scadenza (gg)" type="number"
                     value={editForm.expiry_threshold_days}
                     onChange={(v) => setEditForm({...editForm, expiry_threshold_days: v})}
                     testId="edit-reseller-threshold" />
              <Input label="Max utenti" type="number" value={editForm.max_users}
                     onChange={(v) => setEditForm({...editForm, max_users: v})}
                     testId="edit-reseller-maxusers" />
            </div>
            <DnsMultiSelect
              label="DNS assegnati a questo reseller"
              allDns={allDns}
              selected={editForm.allowed_dns_ids}
              onChange={(ids) => setEditForm({...editForm, allowed_dns_ids: ids})}
              testId="reseller-edit-dns" />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={editForm.is_active}
                     onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                     data-testid="edit-reseller-active" />
              <span>Attivo (deselezionare per sospendere)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={editForm.vpn_popup_enabled}
                     onChange={(e) => setEditForm({...editForm, vpn_popup_enabled: e.target.checked})}
                     data-testid="edit-reseller-vpn-popup" />
              <span>Avviso VPN all'avvio (per gli utenti di questo reseller)</span>
            </label>
            <ModalButtons onCancel={() => setShowEditModal(false)} submitText="Salva modifiche"
                          testId="reseller-edit-submit" />
          </form>
        </Modal>
      )}

      {toast && (
        <Toast {...toast} onClose={() => setToast(null)} />
      )}
    </div>
  );
}

// ============================================================================
// Drill-down: utenti di uno specifico reseller
// ============================================================================
function ResellerUsersView({ reseller, onBack }) {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/resellers/${reseller.id}/users`, {
          params: search ? { search } : {}
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setData([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reseller.id, search]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          data-testid="back-to-resellers"
          onClick={onBack}
          className="p-2 hover:bg-zinc-800 rounded transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Utenti di <span className="text-netflix-red">{reseller.username}</span></h1>
          <p className="text-xs text-zinc-500">{data.total} utenti totali</p>
        </div>
      </div>

      <div className="bg-amber-950/40 border border-amber-800/50 rounded-sm p-3 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
        <p className="text-xs text-amber-200">
          Stai vedendo gli utenti di un reseller. Solo lui può modificarli dal proprio pannello.
          Per supporto, contatta il reseller direttamente.
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input
          type="text"
          placeholder="Cerca utente…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-zinc-900 border border-zinc-800 rounded-sm focus:border-netflix-red outline-none text-sm"
        />
      </div>

      <div className="bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-900 text-zinc-400 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Username</th>
              <th className="text-left px-4 py-3">DNS</th>
              <th className="text-left px-4 py-3">Devices</th>
              <th className="text-left px-4 py-3">Scadenza</th>
              <th className="text-left px-4 py-3">Stato</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {loading ? (
              <tr><td colSpan="5" className="text-center py-8 text-zinc-500">Caricamento…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan="5" className="text-center py-8 text-zinc-500">Nessun utente</td></tr>
            ) : data.items.map((u) => (
              <tr key={u.id} className="hover:bg-zinc-900/50 transition-colors">
                <td className="px-4 py-3 font-mono text-sm">{u.username}</td>
                <td className="px-4 py-3 text-xs text-zinc-400">{u.dns_name || "—"}</td>
                <td className="px-4 py-3 text-sm">{u.devices_count}/{u.max_devices}</td>
                <td className="px-4 py-3 text-xs">{u.expiration_date?.slice(0, 10) || "—"}</td>
                <td className="px-4 py-3">
                  {u.is_active ? (
                    <span className="text-green-500 text-xs font-bold">● ATTIVO</span>
                  ) : (
                    <span className="text-zinc-500 text-xs font-bold">● INATTIVO</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================
function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-zinc-950 border border-zinc-800 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </motion.div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label className="block text-xs text-zinc-400 mb-1">{label}</label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-zinc-800 border border-zinc-700 rounded-sm px-3 py-2 text-white focus:border-netflix-red outline-none text-sm"
      />
    </div>
  );
}

function ModalButtons({ onCancel, submitText, testId }) {
  return (
    <div className="flex gap-3 pt-4">
      <button type="button" onClick={onCancel}
              className="flex-1 bg-zinc-800 hover:bg-zinc-700 rounded-sm px-4 py-2 transition-colors">
        Annulla
      </button>
      <button data-testid={testId} type="submit"
              className="flex-1 bg-netflix-red hover:bg-red-700 font-bold rounded-sm px-4 py-2 transition-colors">
        {submitText}
      </button>
    </div>
  );
}

function DnsMultiSelect({ label, allDns, selected, onChange, testId }) {
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };
  return (
    <div data-testid={testId}>
      <label className="block text-sm text-zinc-300 mb-2">{label}</label>
      {allDns.length === 0 ? (
        <p className="text-xs text-zinc-500 italic">Nessun DNS configurato. Aggiungili dalla sezione "DNS Servers".</p>
      ) : (
        <div className="max-h-40 overflow-y-auto bg-zinc-900 border border-zinc-700 rounded p-2 space-y-1">
          {allDns.map((d) => {
            const checked = selected.includes(d.id);
            return (
              <label key={d.id} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm transition-colors ${checked ? 'bg-red-900/30 text-white' : 'hover:bg-zinc-800 text-zinc-300'}`}>
                <input type="checkbox" checked={checked}
                       onChange={() => toggle(d.id)}
                       data-testid={`${testId}-cb-${d.id}`}
                       className="accent-red-600" />
                <span className="font-medium">{d.name}</span>
              </label>
            );
          })}
        </div>
      )}
      <p className="text-[11px] text-zinc-500 mt-1">
        Il reseller potrà assegnare ai propri utenti SOLO i DNS qui selezionati. Vedrà solo l'etichetta, mai l'URL.
      </p>
    </div>
  );
}

function Toast({ msg, type, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      className={`fixed bottom-4 right-4 px-6 py-3 rounded-sm shadow-lg z-50 flex items-center gap-3
        ${type === "success" ? "bg-green-600" : "bg-netflix-red"}`}
    >
      {type === "success" ? <Check className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
      <span className="font-medium">{msg}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-4 h-4" /></button>
    </motion.div>
  );
}
